/**
 * Service for managing demo pairs (predefined input→output image pairs).
 * Used for demonstration purposes: when a user uploads a matching input image,
 * the edge function returns the predefined output instead of calling Gemini.
 */

import { supabase } from '@/lib/supabase';
import { DemoPair } from '@/types';

const STORAGE_BUCKET = 'images';

// ============================================================================
// Perceptual Hash (Average Hash, 8×8 = 64-bit)
// Uses browser Canvas API — same algorithm as OffscreenCanvas in the edge function
// ============================================================================

export async function computeAHash(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 8;
        canvas.height = 8;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas 2D context unavailable')); return; }

        ctx.drawImage(img, 0, 0, 8, 8);
        const { data } = ctx.getImageData(0, 0, 8, 8);

        const grays: number[] = [];
        for (let i = 0; i < 64; i++) {
          grays.push(0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]);
        }

        const mean = grays.reduce((s, v) => s + v, 0) / 64;

        let hash = 0n;
        for (let i = 0; i < 64; i++) {
          if (grays[i] >= mean) hash |= 1n << BigInt(63 - i);
        }

        resolve(hash.toString(16).padStart(16, '0'));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for hashing'));
    };

    img.src = objectUrl;
  });
}

export async function computeAHashFromUrl(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch image for hashing: ${response.status}`);
  const blob = await response.blob();
  const file = new File([blob], 'image', { type: blob.type || 'image/jpeg' });
  return computeAHash(file);
}

// ============================================================================
// CRUD
// ============================================================================

export async function listDemoPairs(): Promise<DemoPair[]> {
  const { data, error } = await supabase
    .from('demo_pairs')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data ?? []).map((row) => {
    const { data: inputUrlData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(row.input_storage_path);
    const { data: outputUrlData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(row.output_storage_path);
    return {
      id: row.id,
      inputStoragePath: row.input_storage_path,
      inputPhash: row.input_phash,
      outputStoragePath: row.output_storage_path,
      method: row.method as 'empty' | 'staged',
      label: row.label ?? null,
      userIds: row.user_ids ?? [],
      createdAt: row.created_at,
      inputUrl: inputUrlData.publicUrl,
      outputUrl: outputUrlData.publicUrl,
    };
  });
}

export async function updateDemoPairUsers(id: string, userIds: string[]): Promise<void> {
  const { error } = await supabase.from('demo_pairs').update({ user_ids: userIds }).eq('id', id);
  if (error) throw error;
}

export async function createDemoPair(
  inputFile: File,
  outputFile: File,
  method: 'empty' | 'staged',
  label: string,
  userIds: string[] = []
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const pairId = crypto.randomUUID();
  const ext = (f: File) => f.type === 'image/png' ? 'png' : 'jpg';
  const inputPath = `demo-pairs/${pairId}/input.${ext(inputFile)}`;
  const outputPath = `demo-pairs/${pairId}/output.${ext(outputFile)}`;

  // Compute hash and upload in parallel
  const [inputPhash, inputUpload, outputUpload] = await Promise.all([
    computeAHash(inputFile),
    supabase.storage.from(STORAGE_BUCKET).upload(inputPath, inputFile, {
      contentType: inputFile.type,
      upsert: false,
    }),
    supabase.storage.from(STORAGE_BUCKET).upload(outputPath, outputFile, {
      contentType: outputFile.type,
      upsert: false,
    }),
  ]);

  if (inputUpload.error) throw new Error(`Input-Upload fehlgeschlagen: ${inputUpload.error.message}`);
  if (outputUpload.error) throw new Error(`Output-Upload fehlgeschlagen: ${outputUpload.error.message}`);

  const { error } = await supabase.from('demo_pairs').insert({
    id: pairId,
    input_storage_path: inputPath,
    input_phash: inputPhash,
    output_storage_path: outputPath,
    method,
    label: label.trim() || null,
    user_ids: userIds,
    created_by: user.id,
  });

  if (error) throw error;
}

export async function deleteDemoPair(id: string): Promise<void> {
  const { data, error: fetchError } = await supabase
    .from('demo_pairs')
    .select('input_storage_path, output_storage_path')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  const { error } = await supabase.from('demo_pairs').delete().eq('id', id);
  if (error) throw error;

  // Remove storage files (non-fatal)
  await supabase.storage.from(STORAGE_BUCKET).remove([
    data.input_storage_path,
    data.output_storage_path,
  ]);
}
